import React, { useEffect, useState } from 'react';
import { t } from 'i18next';
import Button from '@mui/material/Button';
import Skeleton from '@mui/material/Skeleton';
import axios from 'axios';

interface TasksProps {
  companyID?: number;
}

const Payment: React.FC<TasksProps> = ({ companyID }) => {
  const [loading, setLoading] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [paymentsLoading, setPaymentsLoading] = useState(true);
  const [subscriptionLoading, setSubscriptionLoading] = useState(true);

  const [payments, setPayments] = useState<any[]>([]);
  const [subscription, setSubscription] = useState<any>(null);

  useEffect(() => {
    if (!companyID) return;

    getPayments();
    getSubscription();
  }, [companyID]);

  const getPayments = async () => {
    try {
      setPaymentsLoading(true);

      // companyId сервер берёт из токена — свой биллинг запрашивается без id
      const { data } = await axios.get('/billing/payments');

      setPayments(data.data || []);
      console.log('payments:', data);
    } catch (error) {
      console.error('PAYMENTS ERROR:', error);
    } finally {
      setPaymentsLoading(false);
    }
  };

  const getSubscription = async () => {
    try {
      setSubscriptionLoading(true);

      const { data } = await axios.get('/billing/subscription');

      setSubscription(data.subscription || null);
      console.log('subscription:', data);
    } catch (error) {
      console.error('SUBSCRIPTION ERROR:', error);
    } finally {
      setSubscriptionLoading(false);
    }
  };

  const handleSubscribe = async () => {
    try {
      setLoading(true);

      const { data } = await axios.post('/billing/subscribe', {
        priceId: 'price_1TSuaeGxuf0K2NbtXEefVEwH',
      });

      if (data.url) {
        window.location.href = data.url;
      }
    } catch (error) {
      console.error('SUBSCRIBE ERROR:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    try {
      if (!subscription?.id) {
        console.log('No subscription ID');
        return;
      }

      setCancelLoading(true);

      const body = {
        subscriptionId: subscription.id,
      };

      console.log('SEND BODY:', body);

      const { data } = await axios.post('/billing/cancel-subscription', {});

      console.log('CANCEL RESPONSE:', data);

      await getSubscription();
      await getPayments();
    } catch (error: any) {
      console.error('CANCEL SUBSCRIPTION ERROR:', error);

      if (error.response) {
        console.log('ERROR RESPONSE:', error.response.data);
        console.log('ERROR STATUS:', error.response.status);
      }
    } finally {
      setCancelLoading(false);
    }
  };

  const isCancelled = Boolean(subscription?.cancel_at_period_end);

  return (
    <div>
      {/* subscription */}
      <div className="mb-6 rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h2 className="mb-2 text-lg font-semibold text-gray-800 dark:text-gray-200">
              Subscription
            </h2>

            {subscriptionLoading ? (
              <div className="space-y-2">
                <Skeleton variant="text" width={180} />
                <Skeleton variant="text" width={220} />
                <Skeleton variant="text" width={200} />
              </div>
            ) : subscription ? (
              <div className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
                <p>
                  <strong>Status:</strong>{' '}
                  <span
                    className={`rounded-full px-2 py-1 text-xs font-medium ${
                      isCancelled
                        ? 'bg-red-100 text-red-700'
                        : subscription.status === 'active'
                        ? 'bg-green-100 text-green-700'
                        : 'bg-gray-100 text-gray-700'
                    }`}
                  >
                    {isCancelled ? 'Cancelled' : subscription.status}
                  </span>
                </p>

                <p>
                  <strong>Price:</strong>{' '}
                  {(subscription.plan?.amount / 100).toFixed(2)}{' '}
                  {subscription.currency?.toUpperCase()} /{' '}
                  {subscription.plan?.interval}
                </p>

                <p>
                  <strong>Next billing:</strong>{' '}
                  {subscription.items?.data?.[0]?.current_period_end
                    ? new Date(
                        subscription.items.data[0].current_period_end * 1000
                      ).toLocaleDateString()
                    : '-'}
                </p>

                {isCancelled && (
                  <p className="text-sm text-red-500">
                    Subscription will end on{' '}
                    {subscription.cancel_at
                      ? new Date(subscription.cancel_at * 1000).toLocaleDateString()
                      : '-'}
                  </p>
                )}
              </div>
            ) : (
              <p className="text-gray-500 dark:text-gray-400">
                No active subscription
              </p>
            )}
          </div>

          <div className="flex items-center gap-2">
            <Button
              variant="contained"
              onClick={handleSubscribe}
              disabled={loading || !companyID}
              sx={{
                borderRadius: '10px',
                textTransform: 'none',
                px: 3,
                py: 1,
                minWidth: '140px',
              }}
            >
              {loading ? 'Loading...' : 'Subscribe'}
            </Button>

            {subscription?.id &&
              subscription?.status === 'active' &&
              !isCancelled && (
                <Button
                  variant="outlined"
                  color="error"
                  onClick={handleCancelSubscription}
                  disabled={cancelLoading}
                  sx={{
                    borderRadius: '10px',
                    textTransform: 'none',
                    px: 3,
                    py: 1,
                    minWidth: '140px',
                  }}
                >
                  {cancelLoading ? 'Cancelling...' : 'Cancel'}
                </Button>
              )}
          </div>
        </div>
      </div>

      {/* payments */}
      <div>
        <h2 className="mb-4 text-lg font-semibold text-gray-800 dark:text-gray-200">
          {t('paymentHistory') || 'Payment History'}
        </h2>

        {paymentsLoading ? (
          <div className="space-y-2">
            {[1, 2, 3].map((item) => (
              <div
                key={item}
                className="flex items-center justify-between rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 dark:border-gray-700 dark:bg-gray-700"
              >
                <div className="flex items-center gap-4">
                  <Skeleton variant="text" width={100} />
                  <Skeleton variant="text" width={120} />
                  <Skeleton variant="text" width={180} />
                </div>

                <Skeleton variant="rounded" width={80} height={26} />
              </div>
            ))}
          </div>
        ) : payments.length > 0 ? (
          <div className="space-y-2">
            {payments.map((payment: any) => (
              <div
                key={payment.id}
                className="flex items-center justify-between rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 dark:border-gray-700 dark:bg-gray-700"
              >
                <div className="flex flex-wrap items-center gap-4 text-sm">
                  <span className="font-medium text-gray-800 dark:text-gray-200">
                    {(payment.amount_received / 100).toFixed(2)}{' '}
                    {payment.currency?.toUpperCase()}
                  </span>

                  <span className="text-gray-500 dark:text-gray-400">
                    {new Date(payment.created * 1000).toLocaleDateString()}
                  </span>

                  {payment.description && (
                    <span className="text-gray-500 dark:text-gray-400">
                      {payment.description}
                    </span>
                  )}
                </div>

                <span
                  className={`rounded-full px-3 py-1 text-xs font-medium ${
                    payment.status === 'succeeded'
                      ? 'bg-green-100 text-green-700'
                      : payment.status === 'processing'
                      ? 'bg-yellow-100 text-yellow-700'
                      : 'bg-red-100 text-red-700'
                  }`}
                >
                  {payment.status}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <div className="rounded-lg bg-gray-50 p-6 text-center dark:bg-gray-700">
            <p className="text-gray-600 dark:text-gray-400">
              {t('paymentHistoryComingSoon') ||
                'Payment history will be available soon'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Payment;